// Dependencies imports
import {
  Box,
  Stack,
  DialogContent,
  CircularProgressProps,
  CircularProgress,
  Typography,
  Paper
} from '@mui/material';
import { useConfig, useVideoUpload } from 'hooks/config';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { IconButton, Slider, Link } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { RootState } from 'store';
import moment from 'moment';

// Local imports
import PickAndPlaceImage from '../../assets/images/pick_and_place.gif';
import SpatioTemporalImage from '../../assets/images/spatio_temporal.gif';
import { useSnack } from 'plugins/snack';
import { Videos } from 'store/inference/schema';
import useVideo from 'hooks/video';
import SelectConfigurationPlaceholder from '../../assets/images/placeholder.svg';
import { ITableHeadColumn, Table, Modal } from 'components';

// Style imports
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setPreventNavigation } from 'store/preventNavigation/preventNavigationSlice';
import {
  useDeleteSequenceModel,
  useGetConfiguration,
  useGetTrainingVideos,
  useListConfigurations,
  useListInferenceModels,
  useUpdateConfigType
} from 'api/sdk';
import DependancyGraph from '../configuration/AnalysisConfiguration/WorkflowMonitor/dependancyGraph';
import SearchSelect from 'components/SearchSelect';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import {
  setFrameIndex,
  setObjectMapping
} from 'store/annotations/annotationSlice';
import { updateConfigId } from 'store/inference';
import clsx from 'clsx';
import {
  setConfiguration,
  setFilteredVideos
} from 'store/configuration/configurationSlice';
import Swal from 'sweetalert2';
import { Search } from 'lucide-react';
import { TrainDropDown } from './trainDropDown';
import WorkflowModal from './workflowModal';

const ActionToolTip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black
  }
}));

const DeleteAction: React.FC<any> = ({ onDelete }) => {
  return (
    <Stack direction={'column'}>
      <div>
        <ActionToolTip title={'Delete'}>
          <IconButton onClick={onDelete} disableRipple>
            <DeleteIcon className="w-[1.4vw]" />
          </IconButton>
        </ActionToolTip>
      </div>
    </Stack>
  );
};

export const TrainPage = () => {
  const { cameras, refresh, getConfigurationData } = useVideoUpload();
  const { mutateAsync: deleteModel, isLoading: deleting } =
    useDeleteSequenceModel();
  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const { data: configurationData } = useGetConfiguration({
    config_id: currentConfigId || ''
  });

  const { mutateAsync: updateConfigType } = useUpdateConfigType();

  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const [isRowEditing, setIsRowEditing] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<any>({
    status: false,
    data: []
  });
  const [openFlowSelection, setOpenFlowSelection] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [columns, setColumns] = useState<any>([]);
  const [rows, setRows] = useState<any>([]);
  const [gridRows, setGridRows] = useState<any>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isPause, setPause] = useState<boolean>(true);
  const [isPlay, setPlay] = useState<boolean>(false);
  const [videoName, setVideoName] = useState<string | null>('');
  const [getVideoCurrentTime, setVicdeoCurrentTime] = useState<number>(0);
  const { videoRef, seek, reset, loading: videoLoading } = useVideo();
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastTime, setLastTime] = useState(0);
  const Snack = useSnack();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(0);
  const [isVideoUploading, setIsVideoUploading] = useState<boolean>(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const { data, isError, refetch } = useListConfigurations();
  const {
    data: modelData,
    isLoading: isModelDataLoading,
    refetch: fetch
  } = useListInferenceModels(
    data?.data.filter(item => item.id === currentConfigId)[0]?.site_id || '',
    {
      config_id: currentConfigId || ''
    }
  );

  useEffect(() => {
    if (cameras && cameras.length > 0) {
      setGridRows(cameras);
      let isUploading = false;

      cameras.every((item: any) => {
        if (item.upload_progress < 100) {
          isUploading = true;
          dispatch(setPreventNavigation(true));
          return false;
        } else {
          return true;
        }
      });
      setIsVideoUploading(isUploading);
    }
  }, [cameras]);
  const [getModelId, setModelId] = useState('');
  const handleInfoClick = (workflows: any[], modelId: string) => {
    setIsOpen({ status: true, data: workflows });
    setModelId(modelId);
    if (workflows && workflows.length > 0) {
      handleChange(workflows[0].id);
    }
  };

  useEffect(() => {
    const updatedColumn: ITableHeadColumn[] = [
      {
        key: 'name',
        value: 'Model Name',
        width: '20%',
        render: (params: any) => {
          return <p className="text-[#102633] text-[0.9vw]">{params?.value}</p>;
        }
      },
      {
        key: 'created_at',
        value: 'Created Date',
        render: (value: any) => {
          return (
            <p className="text-[#102633] text-[0.9vw]">
              {moment(value?.value).format('MMM DD, YYYY : hh:mm A')}
            </p>
          );
        }
      },
      {
        key: 'train_videos',
        value: 'Train Videos',
        render: (value: any) => {
          return <p className="text-[#102633] text-[0.9vw]">{value?.value}</p>;
        }
      },
      {
        key: 'annotations',
        value: 'Activities Annotated',
        align: 'left',
        width: '300px',
        render: (value: any) => {
          return <p className="text-[#102633] text-[0.9vw]">{value?.value}</p>;
        }
      },
      {
        key: 'epochs',
        value: 'Learning Cycles',
        width: '10%',
        render: (value: any) => {
          return (
            <p className="text-[#102633] text-[0.9vw]">
              {value?.value || 0}/{value?.record?.total_epochs}
            </p>
          );
        }
      },
      {
        key: 'status',
        value: 'Status',
        render: (value: any) => {
          return (
            <p
              className={clsx(
                'text-[#102633] text-[0.9vw] font-[Proxima-Nova]',
                value?.value === 'COMPLETED' && 'text-[#1E9605]',
                value?.value === 'FAILED' && 'text-[#BB3433]'
              )}
            >
              {value?.value}
            </p>
          );
        }
      },
      {
        key: 'accuracy',
        value: 'Accuracy',
        render: (value: any) => {
          return (
            <p className="text-[#102633] text-[0.9vw]">
              {(value?.value * 100).toFixed(2)}%
            </p>
          );
        }
      },
      {
        key: 'workflows',
        value: 'Workflows',
        render: (value: any) => {
          return (
            <div className="flex items-center justify-start gap-1">
              <p className="text-[#102633] text-[0.9vw]">
                {/* {value.value.length} */}
              </p>
              <IconButton
                onClick={() => {
                  dispatch(setObjectMapping([]));
                  handleInfoClick(value.value, value.record.id);
                }}
              >
                <InfoOutlinedIcon className="w-[1.2vw] text-black" />
              </IconButton>
            </div>
          );
        }
      },
      {
        key: 'action',
        value: 'Action',
        render: (params: any) => {
          return (
            <DeleteAction
              onDelete={() => {
                handleDeleteModel(params.record?.id);
              }}
            />
          );
        }
      }
    ];
    setColumns(updatedColumn);

    const updatedRows = modelData?.data.map(item => {
      return {
        id: item.id,
        name: item.model_name,
        created_at: item.created_at,
        train_videos: item.trained_videos_count,
        annotations: item.annotationCount,
        epochs: item?.current_epoch,
        status: item?.status,
        accuracy: item?.accuracy,
        workflows: item?.workflow_configurations,
        total_epochs: item?.total_epochs
      };
    });
    setRows(updatedRows);
  }, [data, modelData]);

  const handleDeleteModel = async (id: string) => {
    Swal.fire({
      title: 'Delete Model!',
      text: 'Are you sure you want to delete this model?',
      allowOutsideClick: false,
      confirmButtonText: 'Yes, delete it!',
      showCancelButton: true,
      confirmButtonColor: '#E33030',
      cancelButtonColor: '#102633',
      cancelButtonText: 'No, cancel!',
      focusCancel: true,
      customClass: {
        title: 'swal2-title',
        container: 'swal2-html-container',
        actions: 'swal2-actions'
      }
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          await deleteModel({
            configId: currentConfigId,
            modelId: id
          });
          fetch();
          Snack({
            message: 'Model deleted successfully',
            severity: 'success'
          });
        } catch (error: any) {
          console.log('error', error);
          Snack({
            message:
              error?.response?.data?.detail ===
              'Cannot delete model due to existing dependencies. Constraint: inference_model'
                ? 'Cannot be deleted as the model is used for inference.'
                : 'Oops! something went wrong',
            severity: 'error'
          });
        }
      }
    });
  };

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (isVideoUploading) {
        const confirmationMessage =
          'A video is currently being uploaded. If you leave or refresh the page, the upload will be lost. Are you sure you want to proceed?';
        event.returnValue = confirmationMessage; // Standard for most browsers
        return confirmationMessage; // Required for some browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (isVideoUploading) {
        dispatch(setPreventNavigation(false));
      }
    };
  }, [isVideoUploading]);

  useEffect(() => {
    if (isOpen.status && isOpen.data && isOpen.data.length > 0) {
      const firstWorkflow: any = isOpen.data[0];
      setSelectedWorkflowId(firstWorkflow.id);
      handleChange(firstWorkflow.id);
    }
  }, [isOpen.status]);

  useEffect(() => {
    const checkBuffering = () => {
      const video = videoRef.current;
      if (video) {
        if (
          videoDuration !== 0 &&
          (video.currentTime === videoDuration ||
            video.currentTime > videoDuration)
        ) {
          setPlay(true);
          setPause(false);
        }

        if (video.currentTime > videoDuration) {
          setLoading(false);
        } else if (
          video.currentTime === lastTime &&
          !isPlay &&
          video.currentTime !== videoDuration
        ) {
          setLoading(true);
        } else {
          setLoading(false);
        }
        setLastTime(video.currentTime);
      }
    };

    const interval = setInterval(checkBuffering, 500);

    return () => {
      clearInterval(interval);
    };
  }, [lastTime]);

  const handleSliderChange = (event: Event, value: number | number[]) => {
    const newTime = Array.isArray(value) ? value[0] : value;
    if (newTime === videoDuration) {
      setLoading(false);
    }
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setVicdeoCurrentTime(newTime);
    }
  };

  function videoCurrentTime(event: SyntheticEvent<HTMLVideoElement, Event>) {
    setVicdeoCurrentTime(event?.currentTarget?.currentTime);
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoDuration(videoRef.current.duration);
    }
  };

  const handleClose = () => {
    setPause(true);
    setPlay(false);
    setOpen(false);
    setVideoUrl(null);
  };

  const handlePlay = () => {
    setPlay(false);
    setPause(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handlePause = () => {
    setPlay(true);
    setPause(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Handle selection change
  const handleChange = (selectedValue: string) => {
    setSelectedWorkflowId(selectedValue);
    const filteredModel = isOpen.data.filter(
      (fData: any) => fData?.id === selectedValue
    );
    setSelectedModel(filteredModel.length ? filteredModel : []);
  };

  const handleProceedFunction = (flowType: string) => {
    setOpenFlowSelection(false);
    Swal.fire({
      title: 'Confirm flow type?',
      text: `You are about to select ${flowType} flow type. This action cannot be undone.`,
      allowOutsideClick: false,
      confirmButtonText: 'Proceed',
      showCancelButton: true,
      confirmButtonColor: '#E33030',
      cancelButtonColor: '#102633',
      cancelButtonText: 'Cancel',
      focusCancel: true,
      animation: true,
      customClass: {
        title: 'swal2-title',
        container: 'swal2-html-container',
        actions: 'swal2-actions'
      }
    }).then(result => {
      if (result.isConfirmed) {
        updateConfigType(
          {
            data: {
              config_type: flowType
            },
            params: {
              config_id: currentConfigId || ''
            }
          },
          {
            onSuccess: () => {
              dispatch(setFilteredVideos([]));
              navigate('/train/videos');
            },
            onError: error => {
              // Handle error
              console.error('Failed to update config type:', error);
            }
          }
        );
      }
    });
  };

  return (
    <div className="w-full p-4 flex flex-col gap-2">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">
          Trained Models
        </h2>
        <SearchSelect
          data={[
            ...(data?.data.map((config: any) => ({
              label: config.name,
              value: config.id
            })) || [])
          ]}
          onChange={(value: any) => {
            dispatch(setFrameIndex(1));
            dispatch(setConfiguration(value));
          }}
          width="14vw"
          label="Select Configuration"
          selectValue={currentConfigId}
          placeholder="Configuration"
        />
      </div>
      <div className="w-full">
        {currentConfigId !== 'Select Configuration' ? (
          <Table
            columns={columns}
            rows={rows}
            setIsRowEditing={setIsRowEditing}
            expandedRowIndex={expandedRowIndex}
            containerStyle={{ height: '65vh' }}
            setExpandedRowIndex={setExpandedRowIndex}
            expandedRowRender={record => {
              return <TrainDropDown id={record?.id} />;
            }}
            striped={true}
            loading={isModelDataLoading}
            stickyHeader
            page={page}
            setPage={setPage}
          />
        ) : (
          <div className="w-full flex flex-col items-center justify-center h-[70vh] gap-4">
            <img
              src={SelectConfigurationPlaceholder}
              alt="placeholder"
              className="w-[20vw] h-auto"
            />
            <p className="text-[1.2vw] text-[#707070]">
              Select Configuration type to proceed with the process
            </p>
          </div>
        )}
      </div>
      {currentConfigId !== 'Select Configuration' && (
        <div className="w-full flex items-center justify-center">
          <button
            className="bg-[#102433] flex justify-center items-center w-[14vw] py-[8px] text-white text-[0.8vw] rounded-sm"
            onClick={() => {
              if (!configurationData?.data?.config_type) {
                setOpenFlowSelection(true);
              } else {
                dispatch(setFilteredVideos([]));
                navigate('/train/videos');
              }
            }}
          >
            Train Model
          </button>
        </div>
      )}
      <Modal open={open} onClose={handleClose} size="xl">
        <DialogContent>
          {videoUrl && (
            <Stack>
              <Stack
                direction="row"
                alignItems="center"
                // justifyContent="flex-start"
                width="100%"
              >
                <Typography>{videoName}</Typography>
              </Stack>
              <Stack sx={{ position: 'relative' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                  controls={false}
                  src={videoUrl}
                  onTimeUpdate={videoCurrentTime}
                  onLoadedMetadata={handleLoadedMetadata}
                />
                {loading && (
                  <CircularProgress
                    size={60}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)'
                    }}
                  />
                )}
              </Stack>

              <Stack
                direction={'row'}
                alignItems={'center'}
                gap={2}
                marginTop={3}
              >
                {isPlay && (
                  <IconButton onClick={handlePlay}>
                    <PlayArrowIcon />
                  </IconButton>
                )}
                {isPause && (
                  <IconButton onClick={handlePause}>
                    <PauseIcon />
                  </IconButton>
                )}
                <Slider
                  value={getVideoCurrentTime}
                  max={videoDuration}
                  onChange={handleSliderChange}
                  aria-labelledby="continuous-slider"
                  style={{ width: '100%' }}
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Modal>
      <Modal
        open={openFlowSelection}
        onClose={() => setOpenFlowSelection(false)}
        size="lg"
      >
        <div className="flex flex-col md:flex-row items-start justify-between gap-6 p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex flex-col gap-4 flex-1 min-w-0">
            <h3 className="text-base md:text-lg lg:text-xl font-[Proxima-Nova-Bold]">
              Spatio-Temporal
            </h3>
            <p className="text-sm md:text-base text-gray-700">
              The Spatio-Temporal flow captures the complex activities by
              analyzing the activities over time in dynamic environments
            </p>
            <div className="relative w-full aspect-video">
              <img
                className="w-full h-full object-cover rounded-md"
                alt="Spatio-Temporal visualization"
                src={SpatioTemporalImage}
              />
            </div>
            <div className="w-full flex items-center justify-center mt-2">
              <button
                className="bg-[#102433] flex justify-center items-center w-full max-w-[200px] py-2 text-white text-sm rounded-sm hover:bg-[#1a3a4f] transition-colors"
                onClick={() => {
                  handleProceedFunction('spatio_temporal');
                }}
              >
                Select
              </button>
            </div>
          </div>

          <div className="hidden md:block h-auto w-[2px] bg-gray-200 self-stretch mx-2"></div>

          <div className="flex flex-col gap-4 flex-1 min-w-0">
            <h3 className="text-base md:text-lg lg:text-xl font-[Proxima-Nova-Bold]">
              Pick and Place
            </h3>
            <p className="text-sm md:text-base text-gray-700">
              The Pick and Place flow handles simple, repetitive tasks by
              accurately detecting individual component activities
            </p>
            <div className="relative w-full aspect-video">
              <img
                className="w-full h-full object-cover rounded-md"
                alt="Pick and Place visualization"
                src={PickAndPlaceImage}
              />
            </div>
            <div className="w-full flex items-center justify-center mt-2">
              <button
                className="bg-[#102433] flex justify-center items-center w-full max-w-[200px] py-2 text-white text-sm rounded-sm hover:bg-[#1a3a4f] transition-colors"
                onClick={() => {
                  handleProceedFunction('pick_and_place');
                }}
              >
                Select
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <WorkflowModal
        handleChange={handleChange}
        setIsOpen={setIsOpen}
        setSelectedWorkflowId={setSelectedWorkflowId}
        setSelectedModel={setSelectedModel}
        isOpen={isOpen}
        selectedWorkflowId={selectedWorkflowId}
        selectedModel={selectedModel}
        getConfigurationData={getConfigurationData}
        getModelId={getModelId}
        currentConfigId={currentConfigId}
      />
    </div>
  );
};
